import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink } from '@trpc/client';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../../../backend/src/trpc/routers/';

// Create tRPC React hook instance
export const trpc = createTRPCReact<AppRouter>();

// Export types for input/output inference
type RouterInput = inferRouterInputs<AppRouter>;
type RouterOutput = inferRouterOutputs<AppRouter>;

export const createTrpcClient = () => trpc.createClient({
  links: [
    httpBatchLink({
      url: `${import.meta.env.VITE_API_URL}/trpc`,
      async fetch(url, options) {
        console.log(`Making tRPC request to: ${url}`);

        // Maximum number of retries
        const MAX_RETRIES = 3;

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            const response = await fetch(url, {
              ...options,
              credentials: 'include',
            });

            // If we get a 404 and have retries left, try again
            if (response.status === 404 && attempt < MAX_RETRIES) {
              console.warn(`tRPC request returned 404, retrying (${attempt + 1}/${MAX_RETRIES})`);
              // Exponential backoff
              await new Promise(resolve => setTimeout(resolve, 300 * Math.pow(2, attempt)));
              continue;
            }

            // Handle unauthorized requests
            if (response.status === 401) {
              window.dispatchEvent(new Event('UNAUTHORIZED'));
            }

            return response;
          } catch (err) {
            console.error('tRPC fetch error:', err);
            if (attempt < MAX_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, 300 * Math.pow(2, attempt)));
            } else {
              throw err;
            }
          }
        }

        throw new Error('Failed to fetch after all retries');
      },
    }),
  ],
});

export type { RouterInput, RouterOutput };